import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { AlertTriangle, ShieldCheck, ShieldAlert, Clock3, FileUp, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  getMyVerification,
  submitVerification,
  type VerificationDocument,
  type VerificationDocumentType,
} from '../../api/verification';
import { uploadPrivateDocument, abrirDocumento } from '../../api/uploads';
import { MPL, MPL_FONT_DISPLAY } from '../../styles/mypetlive';

const CCAA = [
  'Andalucía', 'Aragón', 'Asturias', 'Baleares', 'Canarias', 'Cantabria',
  'Castilla-La Mancha', 'Castilla y León', 'Cataluña', 'Comunidad Valenciana',
  'Extremadura', 'Galicia', 'La Rioja', 'Madrid', 'Murcia', 'Navarra',
  'País Vasco', 'Ceuta', 'Melilla',
];

const DOC_TYPES: { value: VerificationDocumentType; label: string }[] = [
  { value: 'nif', label: 'Tarjeta NIF de la entidad' },
  { value: 'association_registry', label: 'Registro de asociaciones' },
  { value: 'animal_protection_registry', label: 'Registro de protección animal' },
  { value: 'zoological_center', label: 'Núcleo zoológico' },
  { value: 'other', label: 'Otro documento' },
];

const docLabel = (type: string) => DOC_TYPES.find(d => d.value === type)?.label || type;

const LEVEL_LABEL: Record<string, string> = {
  association: 'Asociación',
  animal_protection_entity: 'Entidad de protección animal',
  authorized_center: 'Centro autorizado',
};

// `canReceiveDonations` (src/utils/shelterVerification.ts) solo acepta estos dos
// niveles. Con nivel `association` la protectora queda verificada y puede publicar,
// pero las donaciones siguen cerradas: decirlo evita prometer lo que no se cumple.
const DONATION_LEVELS = ['animal_protection_entity', 'authorized_center'];

// Multer corta en 10 MB por archivo y solo acepta imágenes o PDF.
const MAX_DOC_BYTES = 10 * 1024 * 1024;

const BACKEND_ERRORS: Record<string, string> = {
  invalid_file_type: 'Ese archivo no vale: sube una foto (JPG o PNG) o un PDF.',
  missing_file: 'No hemos recibido el archivo. Vuelve a intentarlo.',
  upload_error: 'No se pudo subir el documento. Inténtalo otra vez.',
  unauthorized: 'Tu sesión ha caducado. Vuelve a iniciar sesión.',
};

function backendErrorMessage(error: any, fallback: string) {
  const code = error?.response?.data?.error;
  if (typeof code === 'string' && BACKEND_ERRORS[code]) return BACKEND_ERRORS[code];
  if (error?.response?.status === 413) return 'El archivo es demasiado grande (máximo 10 MB).';
  return fallback;
}

const inputStyle: React.CSSProperties = {
  border: `1px solid ${MPL.border}`,
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 14.5,
  width: '100%',
  background: '#fff',
  color: MPL.ink,
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 13.5, fontWeight: 700, color: MPL.ink }}>
        {label}{required && <span style={{ color: MPL.coral }}> *</span>}
      </span>
      {children}
    </label>
  );
}

// Lo que la verificación bloquea de verdad, en un sitio: sin ella `canPublishAnimals`
// devuelve 403 y ninguna ficha puede salir de borrador, que es lo que más duele.
function BlockedNotice() {
  return (
    <div style={{ fontSize: 13.5, marginTop: 6 }}>
      Mientras no esté aprobada <strong>no puedes publicar ningún animal</strong>: las fichas se quedan en
      borrador y no las ve nadie fuera de tu protectora. Puedes irlas preparando en{' '}
      <Link to="/landlord/animals" style={{ color: 'inherit', fontWeight: 800 }}>Mis animales</Link>.
    </div>
  );
}

function StatusBanner({ status, level, notes }: { status: string; level?: string; notes?: string }) {
  if (status === 'verified') {
    const donationsOn = !!level && DONATION_LEVELS.includes(level);
    return (
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: '#EAF7EF', border: '1px solid #2F855A', borderRadius: 14, padding: '14px 18px', color: '#276749' }}>
        <ShieldCheck size={22} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontWeight: 800 }}>Protectora verificada</div>
          <div style={{ fontSize: 13.5 }}>
            {level && LEVEL_LABEL[level] ? `Nivel: ${LEVEL_LABEL[level]}. ` : ''}
            Ya puedes publicar animales{donationsOn ? ' y recibir donaciones' : ''}.
          </div>
          {!donationsOn && (
            <div style={{ fontSize: 13.5, marginTop: 6 }}>
              Las <strong>donaciones siguen cerradas</strong>: hacen falta el nivel de entidad de protección animal o
              de centro autorizado. Si ya tienes el registro de protección animal o el núcleo zoológico, escríbenos a{' '}
              <a href="mailto:soporte@mypetlive.es" style={{ color: 'inherit', fontWeight: 800 }}>soporte@mypetlive.es</a>{' '}
              para que lo revisemos: reenviar el formulario devolvería tu verificación a “en revisión” y dejarías de
              poder publicar mientras tanto.
            </div>
          )}
        </div>
      </div>
    );
  }
  if (status === 'pending') {
    return (
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: MPL.gold100, border: `1px solid ${MPL.gold}`, borderRadius: 14, padding: '14px 18px', color: MPL.goldDark }}>
        <Clock3 size={22} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontWeight: 800 }}>Verificación en revisión</div>
          <div style={{ fontSize: 13.5 }}>Nuestro equipo está revisando tu documentación. Te avisaremos por email. Puedes corregir y reenviar los datos si lo necesitas.</div>
          <BlockedNotice />
        </div>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: '#F8EAEA', border: '1px solid #C05656', borderRadius: 14, padding: '14px 18px', color: '#8F2F2F' }}>
        <ShieldAlert size={22} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontWeight: 800 }}>Verificación rechazada</div>
          <div style={{ fontSize: 13.5 }}>{notes || 'Revisa los datos y vuelve a enviarla.'}</div>
          <BlockedNotice />
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: MPL.coral100, border: `1px solid ${MPL.coral}`, borderRadius: 14, padding: '14px 18px', color: MPL.coralDark }}>
      <ShieldAlert size={22} style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <div style={{ fontWeight: 800 }}>Tu protectora aún no está verificada</div>
        <div style={{ fontSize: 13.5 }}>Para publicar animales necesitamos comprobar que sois una entidad real. Solo te llevará unos minutos.</div>
        <BlockedNotice />
      </div>
    </div>
  );
}

export default function ShelterVerificationPage() {
  const { user } = useAuth();
  const meId = String(user?._id || '');
  const qc = useQueryClient();

  const { data: verification, isLoading, isError, refetch } = useQuery({
    queryKey: ['my-verification', meId],
    queryFn: () => getMyVerification(meId),
    enabled: !!meId,
  });

  const [form, setForm] = useState({
    legalName: '',
    nif: '',
    autonomousCommunity: '',
    associationRegistryNumber: '',
    animalProtectionRegistryNumber: '',
    zoologicalCenterNumber: '',
    representativeName: '',
    representativeRole: '',
  });
  const [documents, setDocuments] = useState<VerificationDocument[]>([]);
  const [docType, setDocType] = useState<VerificationDocumentType>('nif');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Prefill una sola vez con lo ya enviado (reenvíos tras un rechazo). En efecto y no
  // en el cuerpo del render: así no se encadenan tres setState en fase de render, y el
  // guard `prefilled` sigue evitando que un refetch machaque lo que se está editando.
  useEffect(() => {
    if (prefilled || !verification || verification.status === 'unverified') return;
    setForm(prev => ({
      ...prev,
      legalName: verification.legalName || '',
      nif: verification.nif || '',
      autonomousCommunity: verification.autonomousCommunity || '',
      associationRegistryNumber: verification.associationRegistryNumber || '',
      animalProtectionRegistryNumber: verification.animalProtectionRegistryNumber || '',
      zoologicalCenterNumber: verification.zoologicalCenterNumber || '',
      representativeName: verification.representativeName || '',
      representativeRole: verification.representativeRole || '',
    }));
    setDocuments(verification.documents || []);
    setPrefilled(true);
  }, [verification, prefilled]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.value }));

  const addDocument = async (file?: File | null) => {
    if (!file) return;
    // Lo mismo que aplica multer: sin esto un PDF de 12 MB fallaba con un mensaje
    // genérico y no había forma de saber que el problema era el peso.
    const allowed = file.type.startsWith('image/') || file.type === 'application/pdf';
    if (!allowed) {
      toast.error('Solo aceptamos imágenes (JPG, PNG) o PDF.');
      return;
    }
    if (file.size > MAX_DOC_BYTES) {
      toast.error('El archivo pesa más de 10 MB. Sube una versión más ligera.');
      return;
    }
    setUploading(true);
    try {
      // A la zona privada (AUD-5): solo tú y el equipo de MyPetLive podéis verlo.
      const { url } = await uploadPrivateDocument(file);
      setDocuments(prev => [...prev, { type: docType, fileUrl: url, status: 'pending' }]);
    } catch (error: any) {
      // El código crudo del backend (`invalid_file_type`) se pintaba tal cual.
      toast.error(backendErrorMessage(error, 'No se pudo subir el documento.'));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.legalName.trim() || !form.nif.trim() || !form.autonomousCommunity || !form.representativeName.trim()) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    if (!documents.length) {
      toast.error('Adjunta al menos un documento (p. ej. la tarjeta NIF)');
      return;
    }
    setSending(true);
    try {
      await submitVerification(meId, { ...form, documents });
      toast.success('Verificación enviada. La revisaremos lo antes posible.');
      qc.invalidateQueries({ queryKey: ['my-verification', meId] });
    } catch (error: any) {
      toast.error(backendErrorMessage(error, 'No se pudo enviar la verificación.'));
    } finally {
      setSending(false);
    }
  };

  const status = verification?.status || 'unverified';
  const isVerified = status === 'verified';
  // El formulario solo se ofrece cuando sabemos de verdad que falta verificación.
  const showForm = !!meId && !isLoading && !isError && !isVerified;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', display: 'grid', gap: 18, color: MPL.ink }}>
      <header>
        <h1 style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 28, fontWeight: 800, margin: 0 }}>
          Verificación de la protectora
        </h1>
        <p style={{ color: MPL.muted, margin: '6px 0 0', fontSize: 14.5 }}>
          Los datos legales de tu entidad nos permiten activar la publicación de animales y las donaciones.
        </p>
      </header>

      {/* Sin estado de error, un fallo de red se leía como "aún no estás verificada":
          una protectora ya aprobada veía el formulario en blanco y podía reenviarlo,
          devolviéndose a "en revisión" y perdiendo el permiso de publicar. */}
      {!meId ? (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: MPL.coral100, border: `1px solid ${MPL.coral}`, borderRadius: 14, padding: '14px 18px', color: MPL.coralDark }}>
          <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontWeight: 800 }}>No hemos podido identificar tu cuenta</div>
            <div style={{ fontSize: 13.5 }}>Vuelve a iniciar sesión para gestionar la verificación de tu protectora.</div>
          </div>
        </div>
      ) : isLoading ? (
        <div style={{ color: MPL.muted }}>Cargando estado…</div>
      ) : isError ? (
        <div style={{ display: 'grid', gap: 10, justifyItems: 'start', background: '#fff', border: `1px solid ${MPL.border}`, borderRadius: 14, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800 }}>
            <AlertTriangle size={18} color={MPL.coralDark} />
            No hemos podido consultar el estado de tu verificación
          </div>
          <div style={{ color: MPL.muted, fontSize: 13.5 }}>
            No te decimos que estás sin verificar porque no lo sabemos. Puede ser un problema de conexión.
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            style={{ background: '#fff', border: `1px solid ${MPL.border}`, borderRadius: 10, padding: '9px 18px', fontSize: 13.5, fontWeight: 800, color: MPL.tealDark, cursor: 'pointer' }}
          >
            Reintentar
          </button>
        </div>
      ) : (
        <StatusBanner status={status} level={verification?.verificationLevel} notes={verification?.notes} />
      )}

      {showForm && (
        <form onSubmit={submit} style={{ display: 'grid', gap: 16, background: '#fff', border: `1px solid ${MPL.border}`, borderRadius: 18, padding: 22 }}>
          <h2 style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 19, fontWeight: 800, margin: 0 }}>Datos de la entidad</h2>
          <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            <Field label="Nombre legal" required>
              <input style={inputStyle} value={form.legalName} onChange={set('legalName')} placeholder="Asociación Protectora…" />
            </Field>
            <Field label="NIF" required>
              <input style={inputStyle} value={form.nif} onChange={set('nif')} placeholder="G12345678" />
            </Field>
            <Field label="Comunidad autónoma" required>
              <select style={inputStyle} value={form.autonomousCommunity} onChange={set('autonomousCommunity')}>
                <option value="">Selecciona…</option>
                {CCAA.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Nº registro de asociaciones">
              <input style={inputStyle} value={form.associationRegistryNumber} onChange={set('associationRegistryNumber')} />
            </Field>
            <Field label="Nº registro de protección animal">
              <input style={inputStyle} value={form.animalProtectionRegistryNumber} onChange={set('animalProtectionRegistryNumber')} />
            </Field>
            <Field label="Nº de núcleo zoológico">
              <input style={inputStyle} value={form.zoologicalCenterNumber} onChange={set('zoologicalCenterNumber')} />
            </Field>
            <Field label="Persona representante" required>
              <input style={inputStyle} value={form.representativeName} onChange={set('representativeName')} placeholder="Nombre y apellidos" />
            </Field>
            <Field label="Cargo">
              <input style={inputStyle} value={form.representativeRole} onChange={set('representativeRole')} placeholder="Presidencia, secretaría…" />
            </Field>
          </div>

          <h2 style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 19, fontWeight: 800, margin: '6px 0 0' }}>Documentación</h2>
          <p style={{ color: MPL.muted, fontSize: 13.5, margin: 0 }}>
            Sube foto o PDF de al menos un documento oficial (máximo 10 MB por archivo). Con la tarjeta NIF y el
            registro de asociaciones ya podrás publicar animales; el registro de protección animal o el núcleo
            zoológico son los que permiten aprobarte con el nivel que además habilita recibir donaciones.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select style={{ ...inputStyle, width: 'auto' }} value={docType} onChange={e => setDocType(e.target.value as VerificationDocumentType)}>
              {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: MPL.teal, color: '#fff', borderRadius: 10, padding: '10px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: uploading ? 0.6 : 1 }}>
              <FileUp size={16} /> {uploading ? 'Subiendo…' : 'Subir documento'}
              <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} disabled={uploading}
                onChange={e => { addDocument(e.target.files?.[0]); e.currentTarget.value = ''; }} />
            </label>
          </div>
          {documents.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
              {documents.map((doc, i) => (
                <li key={`${doc.fileUrl}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1px solid ${MPL.border}`, borderRadius: 10, padding: '8px 12px', fontSize: 13.5 }}>
                  <span style={{ fontWeight: 700 }}>{docLabel(doc.type)}</span>
                  <button type="button" onClick={() => { abrirDocumento(doc.fileUrl).catch(() => toast.error('No se pudo abrir el archivo')); }}
                    style={{ border: 0, background: 'none', padding: 0, color: MPL.tealDark, font: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>ver archivo</button>
                  <button type="button" onClick={() => setDocuments(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ marginLeft: 'auto', border: 'none', background: 'none', color: '#8F2F2F', cursor: 'pointer', display: 'inline-flex' }}
                    aria-label="Quitar documento">
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div>
            <button type="submit" disabled={sending || uploading}
              style={{ background: MPL.coral, color: '#fff', border: 'none', borderRadius: 12, padding: '12px 26px', fontWeight: 800, fontSize: 15, cursor: 'pointer', opacity: sending ? 0.6 : 1 }}>
              {/* Tras un rechazo también es un reenvío: el botón decía "Enviar". */}
              {sending ? 'Enviando…' : status === 'pending' || status === 'rejected' ? 'Reenviar verificación' : 'Enviar verificación'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
